/**
 * Wishlist — localStorage + legacy cookie (wishlistItems array of handles).
 * Figma 4285:13294 grid; remove control on each card.
 */
(() => {
  const STORAGE_KEY = 'wishlistItems';
  const COOKIE_KEY = 'wishlistItems';
  const ADDED_RESET_MS = 2500;

  /** @returns {string[]} */
  function readWishlist() {
    try {
      // localStorage is authoritative — the cookie is a write-only mirror for
      // the legacy contract and can be truncated by the 4KB cookie cap.
      // Cookie helpers live once in map-utils.js (WIL-461).
      const raw = window.localStorage.getItem(STORAGE_KEY) || window.MapUtils.readCookie(COOKIE_KEY) || '[]';
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((entry) => typeof entry === 'string' && entry.trim());
    } catch {
      return [];
    }
  }

  /** @param {string[]} handles */
  function writeWishlist(handles) {
    const payload = JSON.stringify(handles);
    window.localStorage.setItem(STORAGE_KEY, payload);
    if (handles.length) {
      window.MapUtils.setCookie(COOKIE_KEY, payload);
    } else {
      window.MapUtils.setCookie(COOKIE_KEY, '', -1);
    }
    window.dispatchEvent(new CustomEvent('map:wishlist-updated', { detail: handles }));
  }

  /** Display config read from the panel's data attributes (matches price.liquid). */
  const cfg = {
    gstRate: 1.1,
    moneyFormat: '${{amount}}',
    labels: {
      quantity: 'Quantity',
      addToCart: 'Add to Cart',
      addedToCart: 'Added to cart',
      soldOut: 'Sold out',
      remove: 'Remove from wishlist',
      checkFitment: 'Check Fit',
    },
  };

  /** @param {HTMLElement} panel */
  function readConfig(panel) {
    const d = panel.dataset;
    cfg.gstRate = window.MapUtils.gstRateFromDataset(d);
    if (d.moneyFormat) cfg.moneyFormat = d.moneyFormat;
    if (d.labelQuantity) cfg.labels.quantity = d.labelQuantity;
    if (d.labelAddToCart) cfg.labels.addToCart = d.labelAddToCart;
    if (d.labelAddedToCart) cfg.labels.addedToCart = d.labelAddedToCart;
    if (d.labelSoldOut) cfg.labels.soldOut = d.labelSoldOut;
    if (d.labelRemove) cfg.labels.remove = d.labelRemove;
    if (d.labelCheckFitment) cfg.labels.checkFitment = d.labelCheckFitment;
  }

  /** @param {string} value */
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** GST-inclusive money — maths lives once in map-utils.js (WIL-461). */
  function formatMoney(cents) {
    return window.MapUtils.formatMoney(cents, cfg.gstRate, cfg.moneyFormat);
  }

  /** @param {string[]} tags */
  function classificationBadge(tags) {
    const tag = tags.find((entry) => entry.includes('Part Classification:'));
    if (!tag) return '';
    return tag.replace('Part Classification:', '').trim();
  }

  /** @param {Record<string, unknown>} product */
  function buildCard(product) {
    const handle = String(product.handle || '');
    const title = String(product.title || '');
    const url = `/products/${encodeURIComponent(handle)}`;
    const variant = Array.isArray(product.variants) ? product.variants[0] : null;
    const available = Boolean(variant?.available);
    const price = variant?.price ?? 0;
    const imageSrc = String(product.featured_image || product.images?.[0] || '');
    const tags = Array.isArray(product.tags) ? product.tags : [];
    const badge = classificationBadge(tags);
    const t = cfg.labels;
    const assets = window.__wishlistAssets || {};

    const priceHtml = `<div class="price price--card"><span class="price__current">${formatMoney(price)}</span></div>`;

    const chevronHtml = assets.chevron
      ? `<span class="product-card__qty-chevron-wrap" aria-hidden="true">
              <img class="product-card__qty-chevron" src="${escapeHtml(assets.chevron)}" width="10" height="5" alt="" loading="lazy" decoding="async">
            </span>`
      : '';

    const cartHtml = available
      ? `<form action="/cart/add" method="post" class="product-card__cart-form">
          <input type="hidden" name="id" value="${escapeHtml(String(variant?.id ?? ''))}">
          <div class="product-card__cart" data-product-card-cart>
            <label class="product-card__qty">
              <span class="sr-only">${escapeHtml(t.quantity)}</span>
              <select class="product-card__qty-select" name="quantity" aria-label="${escapeHtml(t.quantity)}">
                ${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => `<option value="${n}"${n === 1 ? ' selected' : ''}>${n}</option>`).join('')}
              </select>
              <span class="product-card__qty-display" aria-hidden="true">1</span>
              ${chevronHtml}
            </label>
            <button
              type="submit"
              class="product-card__add"
              data-product-card-add
              data-label-default="${escapeHtml(t.addToCart)}"
              data-label-added="${escapeHtml(t.addedToCart)}"
            >
              <span class="product-card__add-label">${escapeHtml(t.addToCart)}</span>
              <span class="product-card__add-check" aria-hidden="true">${assets.checkIcon || ''}</span>
            </button>
          </div>
        </form>`
      : `<div class="product-card__cart product-card__cart--sold-out" aria-disabled="true">
          <div class="product-card__qty"><span class="product-card__qty-display">1</span></div>
          <span class="product-card__add"><span>${escapeHtml(t.soldOut)}</span></span>
        </div>`;


    const li = document.createElement('li');
    li.className = 'wishlist-grid__item';
    li.dataset.wishlistItem = handle;
    li.innerHTML = `
      <article class="product-card product-card--linked product-card--wishlist">
        <button type="button" class="wishlist-grid__remove" data-wishlist-remove aria-label="${escapeHtml(t.remove)}">
          <span aria-hidden="true">${assets.removeIcon || ''}</span>
        </button>
        <a class="product-card__link" href="${escapeHtml(url)}" aria-label="${escapeHtml(title)}"></a>
        <div class="product-card__media">
          <div class="product-card__image-wrap">
            ${
              imageSrc
                ? `<img class="product-card__image" src="${escapeHtml(imageSrc)}" alt="${escapeHtml(title)}" width="245" height="184" loading="lazy">`
                : assets.placeholderImage
                  ? `<img class="product-card__image" src="${escapeHtml(assets.placeholderImage)}" alt="${escapeHtml(title)}" width="245" height="184" loading="lazy">`
                  : '<span class="product-card__image-placeholder" aria-hidden="true"></span>'
            }
          </div>
        </div>
        <div class="product-card__info">
          <div class="product-card__header">
            ${badge ? `<div class="product-card__badges"><span class="badge badge--classification">${escapeHtml(badge)}</span></div>` : ''}
            <h3 class="product-card__title">${escapeHtml(title)}</h3>
          </div>
          ${priceHtml}
          <div class="product-card__actions">
            <div class="product-card__fitment-wrap" data-product-card-fitment-root></div>
            ${cartHtml}
          </div>
        </div>
      </article>
    `;
    return li;
  }

  /** Guards concurrent renders — only the latest call may touch the grid. */
  let renderSeq = 0;

  /** @param {HTMLElement} root */
  async function render(root) {
    const empty = root.querySelector('[data-wishlist-empty]');
    const body = root.querySelector('[data-wishlist-body]');
    const grid = root.querySelector('[data-wishlist-grid]');
    if (!empty || !body || !grid) return;

    const seq = ++renderSeq;
    const handles = readWishlist();
    grid.replaceChildren();

    if (!handles.length) {
      empty.hidden = false;
      body.hidden = true;
      return;
    }

    empty.hidden = true;
    body.hidden = false;

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
          if (!response.ok) return null;
          return response.json();
        } catch {
          return null;
        }
      })
    );

    if (seq !== renderSeq) return;

    grid.replaceChildren();
    products.forEach((product) => {
      if (!product) return;
      grid.appendChild(buildCard(product));
    });

    if (!grid.children.length) {
      empty.hidden = false;
      body.hidden = true;
    }

    window.MAPProductCard?.init?.();
  }

  /** @param {HTMLElement} root */
  function bind(root) {
    root.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const removeBtn = target.closest('[data-wishlist-remove]');
      if (!removeBtn) return;

      event.preventDefault();
      event.stopPropagation();

      const item = removeBtn.closest('[data-wishlist-item]');
      const handle = item?.dataset.wishlistItem;
      if (!handle) return;

      // writeWishlist dispatches map:wishlist-updated, which re-renders.
      writeWishlist(readWishlist().filter((entry) => entry !== handle));
    });
  }

  function isDevHost() {
    const host = window.location.hostname;
    return (
      host === '127.0.0.1' ||
      host === 'localhost' ||
      host.endsWith('.shopifypreview.com')
    );
  }

  /** Dev preview only — seeds once so an emptied wishlist stays empty. */
  function seedPreviewWishlist() {
    if (!isDevHost()) return;
    if (window.localStorage.getItem('wishlistSeeded')) return;
    window.localStorage.setItem('wishlistSeeded', '1');
    if (readWishlist().length) return;

    const demo = [
      'halogen-head-lamp-drivers-side-oem-suits-mazda-cx-3-dk-3-2015-on',
      'halogen-head-lamp-passenger-side-certified-suits-toyota-camry-acv40r-2006-to-2009',
      'halogen-head-lamp-passenger-side-oem-suits-mazda-cx-3-dk-3-2015-on',
    ];
    writeWishlist(demo);
  }

  // Single module-level listener: sections can be re-rendered in the theme
  // editor, so resolve the live panel at event time instead of capturing it
  // (a captured panel leaks and re-fetches for detached DOM).
  window.addEventListener('map:wishlist-updated', () => {
    const panel = document.querySelector('[data-wishlist-panel]');
    if (panel instanceof HTMLElement) render(panel);
    syncProductPageToggles();
  });

  function init() {
    const panel = document.querySelector('[data-wishlist-panel]');
    if (!(panel instanceof HTMLElement)) return;
    if (panel.dataset.wishlistInit === 'true') return;
    panel.dataset.wishlistInit = 'true';

    readConfig(panel);

    if (panel.closest('.my-garage--wishlist')) {
      seedPreviewWishlist();
    }

    render(panel);
    bind(panel);
  }

  /** @param {HTMLButtonElement} toggle */
  function toggleHandle(toggle) {
    const root = toggle.closest('[data-product-page]');
    return root instanceof HTMLElement ? root.dataset.productHandle || '' : '';
  }

  /** @param {HTMLButtonElement} toggle @param {boolean} saved */
  function syncProductToggle(toggle, saved) {
    if (toggle.classList.contains('is-added')) return;

    const addLabel = toggle.dataset.wishlistAddLabel || 'Add to wishlist';
    const removeLabel = toggle.dataset.wishlistRemoveLabel || 'Remove from wishlist';
    toggle.classList.toggle('is-saved', saved);
    toggle.setAttribute('aria-pressed', saved ? 'true' : 'false');
    toggle.setAttribute('aria-label', saved ? removeLabel : addLabel);
  }

  /** @type {WeakMap<HTMLButtonElement, number>} */
  const addedFeedbackTimers = new WeakMap();

  /** @param {HTMLButtonElement} toggle */
  function clearAddedFeedback(toggle) {
    const timer = addedFeedbackTimers.get(toggle);
    if (timer) {
      window.clearTimeout(timer);
      addedFeedbackTimers.delete(toggle);
    }
    toggle.classList.remove('is-added');
  }

  /** @param {HTMLButtonElement} toggle */
  function showAddedFeedback(toggle) {
    clearAddedFeedback(toggle);

    const addedLabel = toggle.dataset.wishlistAddedLabel || 'Added to wishlist';
    toggle.classList.remove('is-saved');
    toggle.classList.add('is-added');
    toggle.setAttribute('aria-pressed', 'true');
    toggle.setAttribute('aria-label', addedLabel);

    const timer = window.setTimeout(() => {
      addedFeedbackTimers.delete(toggle);
      toggle.classList.remove('is-added');
      const handle = toggleHandle(toggle);
      syncProductToggle(toggle, Boolean(handle && readWishlist().includes(handle)));
    }, ADDED_RESET_MS);

    addedFeedbackTimers.set(toggle, timer);
  }

  function syncProductPageToggles() {
    document.querySelectorAll('[data-product-wishlist-toggle]').forEach((toggle) => {
      if (!(toggle instanceof HTMLButtonElement)) return;
      const handle = toggleHandle(toggle);
      if (!handle) return;
      syncProductToggle(toggle, readWishlist().includes(handle));
    });
  }

  let productToggleBound = false;

  function bindProductPageToggle() {
    if (productToggleBound) return;
    productToggleBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const toggle = target.closest('[data-product-wishlist-toggle]');
        if (!(toggle instanceof HTMLButtonElement)) return;

        event.preventDefault();
        event.stopPropagation();

        const handle = toggleHandle(toggle);
        if (!handle) return;

        if (readWishlist().includes(handle)) {
          clearAddedFeedback(toggle);
          window.MAPWishlist.remove(handle);
          syncProductToggle(toggle, false);
        } else {
          showAddedFeedback(toggle);
          window.MAPWishlist.add(handle);
        }
      },
      true
    );
  }

  window.MAPWishlist = {
    read: readWishlist,
    write: writeWishlist,
    add(handle) {
      const handles = readWishlist();
      if (handles.includes(handle)) return false;
      writeWishlist([...handles, handle]);
      return true;
    },
    remove(handle) {
      writeWishlist(readWishlist().filter((entry) => entry !== handle));
    },
  };

  function boot() {
    init();
    bindProductPageToggle();
    syncProductPageToggles();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
